import { useCallback } from 'react';
import posthog from 'posthog-js';
import { normSite } from '../lib/storage';
import { splitShotConc } from '../lib/blend';
import { uid, haptic, sanitize, toLocal, fromLocal, isCountedDelivery, isOralDelivery, localDateStr, isCountOnlyPillShot } from '../lib/utils';

// Applies a supply correction delta to a vial's tracked capacity — totalDoses for
// count-based deliveries (pill/pen/autoinjector), totalVol (mL) for draw-based ones
// (syringe/compounded). Pass a negative delta to reverse a previously-applied correction.
function applySupplyDelta(vial, delta, isCounted) {
  if (!delta) return vial;
  return isCounted
    ? { ...vial, totalDoses: Math.round((parseInt(vial.totalDoses) || 0) + delta) }
    : { ...vial, totalVol: parseFloat((parseFloat(vial.totalVol) + delta).toFixed(3)) };
}

export function useShotActions({ stored, upd, user, getCmpd, showToast, logSheetCmpd, setShowLogSheet, setLogSheetCmpd, editing, setEditing, undoShot, setUndoShot, undoBatch, setUndoBatch }) {

  const buildAndCommitShot = useCallback((cmpdKey, data) => {
    const c = getCmpd(cmpdKey);
    const isCounted = isCountedDelivery(c?.deliveryType);
    const isOral = isOralDelivery(c?.deliveryType);
    const num = (stored.counters[cmpdKey] || 0) + 1;
    const isoDate = new Date(data.date || data.logDate).toISOString();
    const _ds = new Date(data.date || data.logDate), _ps = n => String(n).padStart(2, '0');
    const injDate = `${_ds.getFullYear()}-${_ps(_ds.getMonth() + 1)}-${_ps(_ds.getDate())}`;
    const prevActiveVial = data.splitVialId ? (stored.vials[cmpdKey] || []).find(v => v.active && v.status === 'open') : null;
    const prevSplitVial = data.splitVialId ? (stored.vials[cmpdKey] || []).find(v => v.id === data.splitVialId) : null;
    const overfillCorrection = data.overfillCorrection || 0;
    // Counted deliveries never show a vial-picker (data.vial is always null for them —
    // see the "Which vial the correction targets" note below), so the vial-derived
    // value is the fallback; data.pillCount (the log sheet's stepper, or a
    // caller-resolved count from hold-to-log/Log All) wins when supplied.
    const activeVialForPillCount = isOral
      ? (stored.vials[cmpdKey] || []).find(v => v.active && v.status !== 'empty' && v.status !== 'archived') || (stored.vials[cmpdKey] || [])[0]
      : null;
    const shot = {
      id: uid(), date: isoDate, compound: cmpdKey, site: data.injectionSite || null, shotNumber: num,
      protocol: stored.compoundProtocols[cmpdKey] || '', vol: String(data.volume), note: sanitize(data.notes, 500), vial: data.vial || null,
      ...(isOral && {
        pillCount: data.pillCount != null ? (parseInt(data.pillCount) || 1) : (parseInt(activeVialForPillCount?.pillsPerDose) || 1),
        mgPerPill: data.mgPerPill !== undefined ? data.mgPerPill : (parseFloat(activeVialForPillCount?.dosePerInjection) || null),
      }),
      ...(data.splitVialId && {
        splitVialId: data.splitVialId, splitVialPrevStatus: prevSplitVial?.status || 'new',
        splitVialPrevOpenedAt: prevSplitVial?.openedAt || '',
        splitPrevActiveVialId: prevActiveVial?.id || null,
      }),
      ...(data.mgPerMl > 0 && { mgPerMl: data.mgPerMl }),
      ...(overfillCorrection !== 0 && { overfillCorrection }),
    };
    const vials = { ...stored.vials };
    if (vials[cmpdKey]) {
      vials[cmpdKey] = vials[cmpdKey].map(v => {
        if (data.splitVialId) {
          if (v.active && v.status === 'open') {
            const corrected = applySupplyDelta(v, overfillCorrection, isCounted);
            return { ...corrected, status: 'empty', active: false };
          }
          if (v.id === data.splitVialId) return { ...v, status: 'open', openedAt: injDate, active: true, splitOffset: data.splitOffset || 0 };
          return v;
        }
        // The correction targets data.vial (the vial this shot is actually attributed
        // to, i.e. shot.vial) when one is set. Counted deliveries (pill/pen/autoinjector)
        // have no vial-selection dropdown (LogShotSheet only shows it for draw types), so
        // data.vial is always null there -- fall back to "whichever vial is active" for
        // that case, same as this always implicitly assumed. For draw types data.vial IS
        // set, and matching it (rather than blindly "whichever is active") matters once a
        // multi-vial compound's dropdown is used to log against a non-active vial: without
        // this, deleteShot's reversal (which always targets shot.vial) would silently land
        // on the wrong vial instead of undoing exactly what create applied.
        const appliesCorrection = overfillCorrection !== 0 && (data.vial == null || v.id === data.vial);
        if (v.active && v.status === 'open') {
          return appliesCorrection ? applySupplyDelta(v, overfillCorrection, isCounted) : v;
        }
        if (v.active && v.status === 'new') {
          const opened = { ...v, status: 'open', openedAt: injDate };
          return appliesCorrection ? applySupplyDelta(opened, overfillCorrection, isCounted) : opened;
        }
        return appliesCorrection ? applySupplyDelta(v, overfillCorrection, isCounted) : v;
      });
    }
    upd({ userShots: [shot, ...(stored.userShots || [])], counters: { ...stored.counters, [cmpdKey]: num }, vials });
    haptic();
    posthog.capture('shot_logged', { compound: cmpdKey, dose_count: 1 });
    const isPenC = isCountedDelivery(c?.deliveryType);
    showToast(data.splitVialId ? `${c.shortLabel} #${num} logged ✓ — ${isPenC ? 'pen' : 'vial'} transitioned` : `${c.shortLabel} #${num} logged ✓`);
  }, [stored, getCmpd, upd, showToast]);

  const handleLogShotFromSheet = useCallback((data) => {
    if (!user) { showToast('⚠ Not signed in'); return; }
    const isMultiDose = data.multiDose && Array.isArray(data.multiDose);
    if (isMultiDose) {
      const c = getCmpd(logSheetCmpd);
      const doseCount = data.multiDose.length;
      const startNum = (stored.counters[logSheetCmpd] || 0) + 1;
      const isoDate = new Date(data.multiDose[0].date || data.multiDose[0].logDate).toISOString();
      const _d0 = new Date(data.multiDose[0].date || data.multiDose[0].logDate), _p = n => String(n).padStart(2,'0');
      const injDate = `${_d0.getFullYear()}-${_p(_d0.getMonth()+1)}-${_p(_d0.getDate())}`;
      const splitVialId = data.multiDose[0]?.splitVialId || null;
      const splitOffset = data.multiDose[0]?.splitOffset || 0;
      const prevActiveVialMD = splitVialId ? (stored.vials[logSheetCmpd] || []).find(v => v.active && v.status === 'open') : null;
      const prevSplitVialMD = splitVialId ? (stored.vials[logSheetCmpd] || []).find(v => v.id === splitVialId) : null;
      const overfillCorrectionMD = data.multiDose[0]?.overfillCorrection || 0;
      const isCountedMD = isCountedDelivery(c?.deliveryType);
      const shots = data.multiDose.map((dose, idx) => ({
        id: uid(), date: isoDate, compound: logSheetCmpd, site: dose.injectionSite || null,
        shotNumber: startNum + idx, protocol: stored.compoundProtocols[logSheetCmpd] || '',
        vol: String(dose.volume), note: idx === 0 ? sanitize(dose.notes, 500) : '',
        vial: dose.vial || null,
        injectionIndex: idx, totalDoses: doseCount,
        ...(splitVialId && idx === 0 && {
          splitVialId, splitVialPrevStatus: prevSplitVialMD?.status || 'new',
          splitVialPrevOpenedAt: prevSplitVialMD?.openedAt || '',
          splitPrevActiveVialId: prevActiveVialMD?.id || null,
        }),
        // Every injection of a split batch is part of the same two-vial draw, so all carry it.
        ...(dose.mgPerMl > 0 && { mgPerMl: dose.mgPerMl }),
        // Attached to the first shot only, mirroring the splitVialId fields above — the
        // correction is a single event for the whole batch, not per-injection.
        ...(overfillCorrectionMD !== 0 && idx === 0 && { overfillCorrection: overfillCorrectionMD }),
      }));
      const vials = { ...stored.vials };
      if (vials[logSheetCmpd]) {
        vials[logSheetCmpd] = vials[logSheetCmpd].map(v => {
          if (splitVialId) {
            if (v.active && v.status === 'open') {
              const corrected = applySupplyDelta(v, overfillCorrectionMD, isCountedMD);
              return { ...corrected, status: 'empty', active: false };
            }
            if (v.id === splitVialId) return { ...v, status: 'open', openedAt: injDate, active: true, splitOffset };
          } else if (v.active && v.status === 'open') {
            return overfillCorrectionMD !== 0 ? applySupplyDelta(v, overfillCorrectionMD, isCountedMD) : v;
          } else if (v.active && v.status === 'new') {
            const opened = { ...v, status: 'open', openedAt: injDate };
            return overfillCorrectionMD !== 0 ? applySupplyDelta(opened, overfillCorrectionMD, isCountedMD) : opened;
          }
          return v;
        });
      }
      upd({ userShots: [...shots, ...(stored.userShots || [])], counters: { ...stored.counters, [logSheetCmpd]: startNum + doseCount - 1 }, vials });
      haptic(); setShowLogSheet(false); setLogSheetCmpd(null);
      posthog.capture('shot_logged', { compound: logSheetCmpd, dose_count: doseCount });
      showToast(`${c.shortLabel}: ${doseCount} injections logged ✓`);
    } else {
      buildAndCommitShot(logSheetCmpd, data);
      setShowLogSheet(false); setLogSheetCmpd(null);
    }
  }, [stored, logSheetCmpd, user, getCmpd, upd, showToast, setShowLogSheet, setLogSheetCmpd, buildAndCommitShot]);

  const handleOneTapLog = useCallback((cmpdKey, doseAmount, pillCount, mgPerPill) => {
    if (!user) { showToast('⚠ Not signed in'); return; }
    buildAndCommitShot(cmpdKey, { date: new Date().toISOString(), volume: doseAmount, notes: '', ...(pillCount != null && { pillCount }), ...(mgPerPill !== undefined && { mgPerPill }) });
  }, [user, buildAndCommitShot, showToast]);

  // Logs one dose for every entry in one commit -- see the plan's Task 2 Step 3 for why this
  // can't just call buildAndCommitShot/handleOneTapLog in a loop (stale-closure data loss).
  // Mirrors handleLogShotFromSheet's multiDose branch: one stored read, one merged patch, one
  // upd() call -- generalized across different compounds instead of multiple doses of one.
  const handleLogBatch = useCallback((entries) => {
    if (!user) { showToast('⚠ Not signed in'); return; }
    if (!entries || !entries.length) return;
    const isoDate = new Date().toISOString();
    const today = localDateStr();
    const counters = { ...stored.counters };
    const vials = { ...stored.vials };
    const shots = entries.map(({ cmpdKey, doseAmount, pillCount, mgPerPill }) => {
      const num = (counters[cmpdKey] || 0) + 1;
      counters[cmpdKey] = num;
      if (vials[cmpdKey]) {
        vials[cmpdKey] = vials[cmpdKey].map(v => (v.active && v.status === 'new') ? { ...v, status: 'open', openedAt: today } : v);
      }
      // Log All only ever targets pill compounds (DashView's pill tile buckets), so every
      // entry here is oral. Prefer the caller-resolved pillCount/mgPerPill (DashView's
      // defaultPillCount/defaultMgPerPill, which already prefer the last logged value over
      // the vial's current setting) — fall back to deriving from the vial directly for any
      // caller that doesn't supply one.
      const activeVialForPillCount = (vials[cmpdKey] || []).find(v => v.active && v.status !== 'empty' && v.status !== 'archived') || (vials[cmpdKey] || [])[0];
      const resolvedPillCount = pillCount != null ? (parseInt(pillCount) || 1) : (parseInt(activeVialForPillCount?.pillsPerDose) || 1);
      const resolvedMgPerPill = mgPerPill !== undefined ? mgPerPill : (parseFloat(activeVialForPillCount?.dosePerInjection) || null);
      return { id: uid(), date: isoDate, compound: cmpdKey, site: null, shotNumber: num, protocol: stored.compoundProtocols[cmpdKey] || '', vol: String(doseAmount), note: '', vial: null, pillCount: resolvedPillCount, mgPerPill: resolvedMgPerPill };
    });
    upd({ userShots: [...shots, ...(stored.userShots || [])], counters, vials });
    haptic();
    posthog.capture('shot_logged', { compound: 'batch', dose_count: shots.length });
    showToast(`Logged ${shots.length} pill${shots.length === 1 ? '' : 's'} ✓`);
    if (undoBatch?.timer) clearTimeout(undoBatch.timer);
    const timer = setTimeout(() => setUndoBatch(null), 5000);
    setUndoBatch({ shots, timer });
  }, [stored, user, upd, showToast, undoBatch, setUndoBatch]);

  const deleteShot = useCallback(id => {
    const shot = (stored.userShots || []).find(s => s.id === id);
    if (!shot) return;
    const hasSplit = !!(shot?.splitVialId);
    const hasOverfill = !!(shot?.overfillCorrection);
    const hasVialChange = hasSplit || hasOverfill;
    const originalVials = hasVialChange ? stored.vials : null;
    const patch = { userShots: (stored.userShots || []).filter(s => s.id !== id) };
    const isCounted = isCountedDelivery(getCmpd(shot.compound)?.deliveryType);
    if (hasSplit) {
      const cmpd = shot.compound;
      const correction = parseFloat(shot.overfillCorrection) || 0;
      const vialList = (stored.vials[cmpd] || []).map(v => {
        if (v.id === shot.splitVialId) return { ...v, status: shot.splitVialPrevStatus || 'new', openedAt: shot.splitVialPrevOpenedAt || '', active: false, splitOffset: 0 };
        if (v.id === shot.splitPrevActiveVialId) {
          const reverted = applySupplyDelta(v, -correction, isCounted);
          return { ...reverted, status: 'open', active: true };
        }
        return v;
      });
      patch.vials = { ...stored.vials, [cmpd]: vialList };
    } else if (hasOverfill) {
      const cmpd = shot.compound;
      const correction = parseFloat(shot.overfillCorrection);
      // shot.vial is null for counted deliveries (see buildAndCommitShot above) -- fall
      // back to the active, non-archived vial for that case, same as create's fallback.
      // Without this, the correction was never reversed at all for pill/pen/autoinjector
      // shots (v.id === null never matches a real vial id).
      patch.vials = {
        ...stored.vials,
        [cmpd]: (stored.vials[cmpd] || []).map(v => {
          const matches = shot.vial != null ? v.id === shot.vial : (v.active && v.status !== 'archived');
          return matches ? applySupplyDelta(v, -correction, isCounted) : v;
        }),
      };
    }
    upd(patch);
    haptic();
    if (undoShot?.timer) clearTimeout(undoShot.timer);
    const timer = setTimeout(() => setUndoShot(null), 5000);
    setUndoShot({ shot, hasSplit: hasVialChange, originalVials, timer });
  }, [stored, upd, undoShot, setUndoShot, getCmpd]);

  const doUndoShot = useCallback(() => {
    if (!undoShot) return;
    clearTimeout(undoShot.timer);
    const { shot, hasSplit, originalVials } = undoShot;
    upd({ userShots: [...(stored.userShots || []).filter(s => s.id !== shot.id), shot], ...(hasSplit ? { vials: originalVials } : {}) });
    setUndoShot(null);
    showToast('Shot restored ✓');
  }, [undoShot, stored, upd, setUndoShot, showToast]);

  // Matches the existing deleteShot/doUndoShot precedent for a plain (non-split, non-overfill)
  // shot: removes the shots from userShots only. Does NOT revert each compound's counters
  // entry or a vial's 'new'->'open' transition -- deleteShot doesn't do either of those for
  // the equivalent single-shot case either (counters is a monotonically-increasing shot-number
  // sequence, never decremented anywhere in the app), so inventing a reversal only here would
  // be new, untested behavior inconsistent with how a plain shot delete already works.
  const doUndoBatch = useCallback(() => {
    if (!undoBatch) return;
    clearTimeout(undoBatch.timer);
    const ids = new Set(undoBatch.shots.map(s => s.id));
    upd({ userShots: (stored.userShots || []).filter(s => !ids.has(s.id)) });
    setUndoBatch(null);
    showToast('Batch undone ✓');
  }, [undoBatch, stored, upd, setUndoBatch, showToast]);

  const startEdit = useCallback(shot => {
    const cmpd = getCmpd(shot.compound);
    const isCompounded = cmpd?.deliveryType === 'compounded';
    let vol = String(shot.vol || '').replace('ml', '');
    if (isCompounded) {
      const vials = stored.vials?.[shot.compound] || [];
      const vial = vials.find(v => v.id === shot.vial) || vials.find(v => v.status === 'open' || v.active) || vials.find(v => v.conc);
      const conc = splitShotConc(shot, vials, 'compounded') ?? parseFloat(vial?.conc || 0);
      if (conc > 0) vol = String((parseFloat(shot.vol || 0) * conc).toFixed(2));
    }
    setEditing({ ...shot, site: normSite(shot.site), vol, date: toLocal(shot.date) });
  }, [setEditing, stored, getCmpd]);

  const saveEdit = useCallback(() => {
    const newCompound = editing.compound;
    const cmpd = getCmpd(newCompound);
    const isCompounded = cmpd?.deliveryType === 'compounded';
    const isOralEdit = isOralDelivery(cmpd?.deliveryType);
    let vol = editing.vol;
    if (isCompounded) {
      const vials = stored.vials?.[newCompound] || [];
      const vial = vials.find(v => v.id === editing.vial) || vials.find(v => v.status === 'open' || v.active) || vials.find(v => v.conc);
      const original = (stored.userShots || []).find(s => s.id === editing.id);
      const stamped = original?.compound === newCompound ? splitShotConc(original, vials, 'compounded') : null;
      const conc = stamped ?? parseFloat(vial?.conc || 0);
      if (conc > 0) vol = String((parseFloat(editing.vol || 0) / conc).toFixed(3));
    }
    // Editing never changes whether a shot WAS mg-tracked or count-only at logging time
    // (see Global Constraints) — only how many pills/how much mg it represents. Legacy
    // mg-tracked shots with no known mgPerPill (logged before that field existed) still fall
    // through to the plain "DOSE (mg)" input, which writes editing.vol directly — vol above
    // already carries that through unchanged, no special case needed. The count-only case and
    // the mg-tracked-with-known-mgPerPill case both drive a PILLS input (HistoryView.jsx) that
    // already writes editing.vol to match editing.pillCount on every keystroke, so vol is
    // already correct too — the one thing missing from `updates` in either case is pillCount
    // itself, since the base object below never included it.
    const newProtocol = stored.compoundProtocols?.[newCompound] || '';
    const updates = { site: editing.site, vol, note: editing.note, date: fromLocal(editing.date), compound: newCompound, protocol: newProtocol };
    if (isOralEdit && isCountOnlyPillShot(editing) && editing.pillCount != null) {
      updates.pillCount = parseInt(editing.pillCount) || 1;
      // Guarantees vol can never disagree with pillCount in what gets saved: if the input was
      // cleared entirely, editing.vol holds the same unclamped '' while pillCount above clamps
      // to 1 -- without this, the two fields would desync (History displays "1 pills" via a
      // fallback, but Trends/exports would read vol as 0/blank).
      updates.vol = String(updates.pillCount);
    } else if (isOralEdit && !isCountOnlyPillShot(editing) && editing.mgPerPill != null && editing.pillCount != null) {
      // mg-tracked oral shot with a known per-pill mg value: HistoryView.jsx's PILLS input
      // already derived vol (pillCount × mgPerPill) into editing.vol on every keystroke, so
      // vol is already correct -- persist the corrected pillCount too, so calcRemaining's
      // supply math (which sums pillCount, not vol) moves in lockstep with the correction
      // instead of staying stuck at whatever was originally stamped.
      updates.pillCount = parseInt(editing.pillCount) || 1;
    }
    // A split shot's mgPerMl describes its original vials; it means nothing on another stack item.
    upd({ userShots: (stored.userShots || []).map(s => {
      if (s.id !== editing.id) return s;
      const next = { ...s, ...updates };
      if (newCompound !== s.compound) delete next.mgPerMl;
      return next;
    }) });
    setEditing(null); haptic(); showToast("Updated ✓");
  }, [editing, stored, getCmpd, upd, setEditing, showToast]);

  return { handleLogShotFromSheet, handleOneTapLog, handleLogBatch, deleteShot, doUndoShot, doUndoBatch, startEdit, saveEdit };
}
